<script setup>
/**
 * Dashboard Apps.
 *
 * The list of embedded apps on the kit shell: search over the table, the
 * add action in the fixed header, and the same create/edit modal and delete
 * confirmation the page always had.
 */
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { picoSearch } from '@scmmishra/pico-search';

import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSearchInput from 'dashboard/components-next/Settings/SettingsSearchInput.vue';
import { BaseTable } from 'dashboard/components-next/table';
import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmDialog from 'dashboard/components-next/dialog/ConfirmDialog.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import DashboardAppModal from './DashboardAppModal.vue';
import DashboardAppsRow from './DashboardAppsRow.vue';

const store = useStore();
const { t } = useI18n();

const records = useMapGetter('dashboardApps/getRecords');
const uiFlags = useMapGetter('dashboardApps/getUIFlags');

const showDashboardAppPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedApp = ref({});
const mode = ref('CREATE');
const searchQuery = ref('');

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, ['title']);
});

const tableHeaders = computed(() => [
  t('INTEGRATION_SETTINGS.DASHBOARD_APPS.LIST.TABLE_HEADER.NAME'),
  t('INTEGRATION_SETTINGS.DASHBOARD_APPS.LIST.TABLE_HEADER.ENDPOINT'),
  t('INTEGRATION_SETTINGS.DASHBOARD_APPS.LIST.TABLE_HEADER.ACTIONS'),
]);

const noDataMessage = computed(() =>
  searchQuery.value
    ? t('INTEGRATION_SETTINGS.DASHBOARD_APPS.NO_RESULTS')
    : t('INTEGRATION_SETTINGS.DASHBOARD_APPS.LIST.404')
);

onMounted(() => {
  store.dispatch('dashboardApps/get');
});

const toggleDashboardAppPopup = () => {
  showDashboardAppPopup.value = !showDashboardAppPopup.value;
  selectedApp.value = {};
};

const openCreatePopup = () => {
  mode.value = 'CREATE';
  selectedApp.value = {};
  showDashboardAppPopup.value = true;
};

const editApp = app => {
  mode.value = 'UPDATE';
  selectedApp.value = app;
  showDashboardAppPopup.value = true;
};

const openDeletePopup = app => {
  showDeleteConfirmationPopup.value = true;
  selectedApp.value = app;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteApp = async id => {
  try {
    await store.dispatch('dashboardApps/delete', id);
    useAlert(t('INTEGRATION_SETTINGS.DASHBOARD_APPS.DELETE.API_SUCCESS'));
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.DASHBOARD_APPS.DELETE.API_ERROR'));
  }
};

const confirmDeletion = () => {
  closeDeletePopup();
  deleteApp(selectedApp.value.id);
};
</script>

<template>
  <SettingsPageShell
    wide
    :title="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.TITLE')"
    :description="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.DESCRIPTION')"
  >
    <template #actions>
      <Button
        :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.HEADER_BTN_TXT')"
        size="sm"
        @click="openCreatePopup"
      />
    </template>

    <div class="flex items-center justify-between gap-3">
      <SettingsSearchInput
        v-model="searchQuery"
        :placeholder="
          $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.SEARCH_PLACEHOLDER')
        "
      />
      <span v-if="records.length" class="text-sm text-n-slate-11">
        {{
          $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.COUNT', { n: records.length })
        }}
      </span>
    </div>

    <div
      v-if="uiFlags.isFetching"
      role="status"
      aria-busy="true"
      :aria-label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.LIST.LOADING')"
      class="flex flex-col gap-5"
    >
      <Skeleton
        v-for="row in 4"
        :key="row"
        variant="text"
        :lines="2"
        :announce="false"
      />
    </div>

    <BaseTable
      v-else
      :headers="tableHeaders"
      :items="filteredRecords"
      :no-data-message="noDataMessage"
    >
      <template #row="{ items }">
        <DashboardAppsRow
          v-for="(dashboardAppItem, index) in items"
          :key="dashboardAppItem.id"
          :index="index"
          :app="dashboardAppItem"
          @edit="editApp"
          @delete="openDeletePopup"
        />
      </template>
    </BaseTable>

    <DashboardAppModal
      v-if="showDashboardAppPopup"
      :show="showDashboardAppPopup"
      :mode="mode"
      :selected-app-data="selectedApp"
      @close="toggleDashboardAppPopup"
    />

    <ConfirmDialog
      v-model:show="showDeleteConfirmationPopup"
      :title="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.DELETE.TITLE')"
      :description="
        $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.DELETE.MESSAGE', {
          appName: selectedApp.title,
        })
      "
      :confirm-button-label="
        $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.DELETE.CONFIRM_YES')
      "
      :cancel-button-label="
        $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.DELETE.CONFIRM_NO')
      "
      @confirm="confirmDeletion"
      @close="closeDeletePopup"
    />
  </SettingsPageShell>
</template>
